import { useCallback, useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import useDeepCompareEffect from 'use-deep-compare-effect';

import Box from '@mui/material/Box';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useMediaQuery, useTheme } from '@mui/material';

import { ensureDefined } from '@e-socna/utils';

import { environment } from '../../../environment';
import {
  MemoBeforeSubmitForm,
  emptyMemosState,
} from '../../../forms/MemoBeforeSubmit';
import type { PrinterContextProvider_PdfPrint } from '../../../contexts/PrinterContext';

import { TimeManagement } from '../../Illustrations/TimeManagement';
import { SplashScreen } from '../../SplashScreen';
import { SplitButton } from '../../SplitButton';
import { NoData } from '../../Illustrations/NoData';
import { GoingUp } from '../../Illustrations/GoingUp';
import { SpeedDial } from '../../SpeedDial';
import { DisabledFeatureInStrictMode } from '../../DisabledFeatureInStrictMode';
import { PdfViewer } from '../../PdfViewer';
import type { SplitButtonOption } from '../../SplitButton';

import { useDocumentEditorContext } from '../contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import type { Editable } from '../types/editable.type';

export const PreviewTabPanel = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('lg'));

  const {
    enrichedVirtualRoot,
    handleSaveAction,
    props: {
      formSettings: { initialEditable, isLoading },
      getters: { printing: getPrinting },
      handlers: { onPrint, onSavePreview },
      saveSettings: { askMemo },
    },
    state,
  } = useDocumentEditorContext<Editable>();

  const { handlePrint, handleSavePreview } = ensureDefined({
    handlePrint: onPrint,
    handleSavePreview: onSavePreview,
  });

  const [pdfPrint, setPdfPrint] = useState<PrinterContextProvider_PdfPrint>();
  const [isRunningUpdate, setIsRunningUpdate] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const [savePreviewMemos, setSavePreviewMemos] = useState(emptyMemosState);
  const clearSavePreviewMemos = useCallback(() => {
    setSavePreviewMemos(emptyMemosState);
  }, []);

  const { pdfPrints } = getPrinting(initialEditable);
  const lastPrint = pdfPrints[pdfPrints.length - 1];

  const print = useCallback(
    (useCacheFirst: boolean) => {
      handlePrint({
        clearTmp: true,
        handleSuccess: (pdfPrint) => {
          setPdfPrint(pdfPrint);
        },
        isTmp: true,
        memos: emptyMemosState,
        useCacheFirst,
      });
    },
    [handlePrint],
  );

  const savePreviewSubmitHandler = useCallback(
    () =>
      handleSavePreview(state.editable._id, savePreviewMemos, () => {
        clearSavePreviewMemos();
        enqueueSnackbar('Version sauvegardée.');
      }),
    [
      clearSavePreviewMemos,
      enqueueSnackbar,
      handleSavePreview,
      savePreviewMemos,
      state.editable._id,
    ],
  );

  const handleSavePreviewAction = useCallback(() => {
    askMemo?.beforePrint
      ? setSavePreviewMemos((oldMemos) => ({
          ...oldMemos,
          isOpenMemoDialog: true,
        }))
      : savePreviewSubmitHandler();
  }, [askMemo?.beforePrint, setSavePreviewMemos, savePreviewSubmitHandler]);

  const handleSplitButtonClick = useCallback(
    (option: Readonly<SplitButtonOption>) => {
      if (option.key === 'saveBeforePreview') {
        setIsRunningUpdate(true);
        setPdfPrint(undefined);
        handleSaveAction();
        return;
      }

      setPdfPrint(lastPrint);
    },
    [handleSaveAction, lastPrint, setIsRunningUpdate],
  );

  const showSplitButton =
    state.pastVersions.length &&
    state.currentVersion !== state.latestSavedVersion;

  useDeepCompareEffect(() => {
    if (
      !enrichedVirtualRoot.isFullyLoaded ||
      showSplitButton ||
      isRunningUpdate ||
      !enrichedVirtualRoot.subSections.length
    ) {
      return;
    }

    print(true);
  }, [handlePrint, showSplitButton, enrichedVirtualRoot, isRunningUpdate]);

  useEffect(() => {
    if (!isRunningUpdate) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPdfPrint(undefined);
    setIsRunningUpdate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditable.updatedAt]);

  if (!enrichedVirtualRoot.subSections.length) {
    return <NoData />;
  }

  if ((showSplitButton && !pdfPrint) || isRunningUpdate) {
    return (
      <SplashScreen
        illustration={<GoingUp />}
        message="Vous avez effectué des modifications qui n'ont pas encore été sauvegardées."
      >
        <SplitButton
          ariaLabelButton="Choix de l'action appropriée"
          ariaLabelButtonGroup="Action nécessaire avant d'afficher l'aperçu"
          id="action-before-preview"
          onClick={handleSplitButtonClick}
          options={[
            {
              key: 'saveBeforePreview',
              label: "Enregistrer avant d'afficher l'aperçu",
            },
            {
              key: 'previewLastSavedVersion',
              label: "Afficher l'aperçu de la dernière sauvegarde",
            },
          ]}
        />
      </SplashScreen>
    );
  }

  return pdfPrint ? (
    <>
      <SpeedDial
        actions={[
          {
            id: 'refreshPrint',
            icon: <RefreshIcon />,
            name: 'Rafraîchir Impression',
            onClick: () => {
              setPdfPrint(undefined);
              print(false);
            },
          },
          {
            id: 'saveVersion',
            icon: <PictureAsPdfIcon />,
            name: 'Sauvegarder Version',
            onClick: handleSavePreviewAction,
            isDisabled: !lastPrint?.isTmp,
          },
        ]}
        title="Actions possibles sur l'Aperçu d'Impression"
      />
      {!!askMemo?.beforePrint && (
        <MemoBeforeSubmitForm
          clearMemos={clearSavePreviewMemos}
          isLoading={isLoading}
          memos={savePreviewMemos}
          onConfirm={savePreviewSubmitHandler}
          setMemos={setSavePreviewMemos}
          submitLabel="Sauvegarder"
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          maxWidth: '100%',
          mt: matches ? 0 : 2,
          position: 'relative',
        }}
      >
        {/* FIXME: follow this example to implement pageRendering lock
        https://mozilla.github.io/pdf.js/examples/ */}
        <DisabledFeatureInStrictMode>
          <PdfViewer
            showControls
            url={`${environment.backendUrl}/${pdfPrint.file.uri}`}
          />
        </DisabledFeatureInStrictMode>
      </Box>
    </>
  ) : (
    <TimeManagement />
  );
};
